<template>
  <div
    class="text"
    :class="{ 'trial-warning': border }"
  >
    <i
      v-if="border"
      class="hi hi-warning"
    />

    <template v-if="mode === 'simple'">
      <span v-if="text">{{ monthlyTotal || 'No' }} unusual activity {{ pluralize(monthlyTotal, 'instance') }} in {{ monthTitle }}.</span>

      <a
        v-track:click="investigateClicked"
        :href="prevPeriodNoFiltersUrl"
      >
        Investigate now
      </a>
    </template>

    <template v-else-if="mode === 'advanced'">
      <span v-if="hasAny">
        View
        <template v-if="showCurrent">
          <a
            v-track:click="investigateClicked"
            :href="currentPeriodNoFiltersUrl"
          >
            all unusual activity for selected period
          </a>
          ({{ currentNoFiltersTotal }} {{ pluralize(currentNoFiltersTotal, userId ? 'instance' : 'member') }})
        </template>

        <template v-if="showCurrent && showPrevious"> or </template>

        <template v-if="showPrevious">
          <a
            v-track:click="investigateClicked"
            :href="prevPeriodWithFiltersUrl"
          >
            all instances from {{ monthTitle }} with activity classification selected
          </a>
          ({{ monthlyTotal }} {{ pluralize(monthlyTotal, 'instance') }})
        </template>.
      </span>

      <span v-else>No unusual activity</span>
    </template>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import { InsightsDateRanges } from '~/mixins';
import { pluralize } from '~/utils/string_utils';
import { INSIGHTS_CONFIDENCE_LEVELS } from '~/app_constants';
import { mapState, mapGetters } from 'vuex';

dayjs.extend(isBetween);
dayjs.extend(isoWeek);

export default {
  name: 'InsightsUnusualActivityInvestigateNow',
  mixins: [InsightsDateRanges],
  props: {
    mode: {
      type: String,
      required: true,
      validator: (value) => ['simple', 'advanced'].includes(value),
    },
    border: {
      type: Boolean,
      default: false,
    },
    monthlyTotal: {
      type: Number,
      default: 0,
    },
    currentNoFiltersTotal: {
      type: Number,
      default: 0,
    },
    organizationInsightsUrl: {
      type: String,
      required: true,
    },
    period: {
      type: String,
      required: false,
      default: null,
      validator: (value) => [null, 'this_month', 'last_month', 'requested'].includes(value),
    },
    queryDate: {
      type: String,
      required: false,
      default: null,
    },
    teamId: {
      type: Number,
      required: false,
      default: null,
    },
    userId: {
      type: Number,
      required: false,
      default: null,
    },
    text: {
      type: Boolean,
      required: false,
      default: true,
    },
    timeZone: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      investigateClicked: Object.freeze({ label: 'Unusual Investigate now clicked' }),
    };
  },
  computed: {
    ...mapState(['filters']),
    ...mapGetters(['activeTimeZone']),
    showCurrent() { return this.currentNoFiltersTotal > 0; },
    showPrevious() { return this.monthlyTotal > 0; },
    hasAny() { return this.showCurrent || this.showPrevious; },
    linkTimeZone() {
      return this.timeZone || this.activeTimeZone?.for;
    },
    prevPeriodWithFiltersUrl() {
      const url = new URL(this.organizationInsightsUrl);

      this.userId && url.searchParams.set('user_id', `${this.userId}`);
      this.teamId && url.searchParams.set('team_id', `${this.teamId}`);
      url.searchParams.set('tab', 'unusual-activity');
      // Default to a 1-day range based on queryDate when not using month presets
      const baseDate = this.queryDate ? moment(this.queryDate) : moment();

      url.searchParams.set('from', baseDate.format('YYYY-MM-DD'));
      url.searchParams.set('to', baseDate.format('YYYY-MM-DD'));

      if (this.monthsOnly) {
        const currentMonthRange = {
          from: moment().startOf('month'),
          to: moment().startOf('month').add(1, 'month').isAfter(moment(), 'day')
            ? moment().add(1, 'day')
            : moment().add(1, 'month').startOf('month'),
        };
        const previousMonthRange = {
          from: moment().subtract(1, 'month').startOf('month'),
          to: moment().startOf('month'),
        };
        const range = this.period.includes('this') ? currentMonthRange : previousMonthRange;

        url.searchParams.set('from', range.from.format('YYYY-MM-DD'));
        url.searchParams.set('to', range.to.format('YYYY-MM-DD'));
      }

      url.searchParams.set('confidence_levels', this.filters.confidence_levels);
      if (this.linkTimeZone) {
        url.searchParams.set('time_zone', this.linkTimeZone);
      }

      return url;
    },
    currentPeriodNoFiltersUrl() {
      const url = new URL(window.location.href);

      url.searchParams.set('confidence_levels', INSIGHTS_CONFIDENCE_LEVELS.map(level => level.key));
      if (this.linkTimeZone) {
        url.searchParams.set('time_zone', this.linkTimeZone);
      }

      return url;
    },
    prevPeriodNoFiltersUrl() {
      const url = new URL(this.prevPeriodWithFiltersUrl);

      url.searchParams.set('confidence_levels', INSIGHTS_CONFIDENCE_LEVELS.map(level => level.key));

      return url;
    },
    monthsOnly() {
      return ['this_month', 'last_month'].includes(this.period);
    },
    monthTitle() {
      if (this.period === 'this_month') {
        return moment().format('MMMM');
      }

      return moment().subtract(1, 'month').format('MMMM');
    },
  },
  methods: { pluralize }
};
</script>
